import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { PwTestIdDirective } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { AskDecisionOption } from '@core/tellent-assistant-types';
import { ASK_DECISION_TEST_IDS } from '@core/tellent-assistant-util';
import type { RadioGroupItem, Select2Item } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  HeadingComponent,
  MultiselectFeatureDirective,
  MultiSelectTriggerComponent,
  RadioFormGroupComponent,
  Select2Component,
  Select2TriggerComponent,
} from '@core/user-interface-legacy';

const DROPDOWN_THRESHOLD = 5;

/**
 * A question the assistant asks mid-conversation, answered by picking one
 * option — or several when `multi` is set. Single-select: few options render
 * as a radio list, many as a dropdown. Multi-select: a dropdown with the
 * chosen options shown as tags. The chosen option(s) are emitted on confirm
 * and sent back as a plain user turn.
 */
@Component({
  selector: 'tellent-assistant-ask-decision',
  imports: [
    ButtonComponent,
    HeadingComponent,
    MultiselectFeatureDirective,
    MultiSelectTriggerComponent,
    PwTestIdDirective,
    RadioFormGroupComponent,
    ReactiveFormsModule,
    Select2Component,
    Select2TriggerComponent,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="card" [pwTestId]="testIds.card">
      <rt-heading class="question" variant="heading semi-bold">{{ question() }}</rt-heading>

      @if (multi()) {
        <rt-select2
          class="select"
          withMultiselect
          withOverlayLevel
          [pwTestId]="testIds.multiSelect"
          [formControl]="multiControl"
          [items]="selectItems()"
          [disabled]="disabled()"
        >
          <rt-select-trigger-multi
            [placeholder]="'tlc.tellent_assistant.decision.select_placeholder' | translate"
            [disabled]="disabled()"
          />
        </rt-select2>
      } @else if (useDropdown()) {
        <rt-select2
          class="select"
          withOverlayLevel
          [pwTestId]="testIds.dropdownTrigger"
          [items]="selectItems()"
          [disabled]="disabled()"
          (selection)="onSelection($event)"
        >
          <rt-select-trigger
            [placeholder]="'tlc.tellent_assistant.decision.select_placeholder' | translate"
          />
        </rt-select2>
      } @else {
        <rt-radio-form-group
          class="options"
          [items]="radioItems()"
          [formControl]="answerControl"
          [disabled]="disabled()"
        />
      }

      <footer class="actions">
        <rt-button
          variant="plain"
          size="small"
          [pwTestId]="testIds.skip"
          [disabled]="disabled()"
          (click)="skipped.emit()"
        >
          {{ 'tlc.tellent_assistant.proposal.skip' | translate }}
        </rt-button>
        <rt-button
          variant="primary"
          size="small"
          [pwTestId]="testIds.confirm"
          [disabled]="disabled() || !canConfirm()"
          (click)="confirm()"
        >
          {{ confirmText() }}
        </rt-button>
      </footer>
    </section>
  `,
  styleUrl: './ask-decision.component.less',
})
export class TellentAssistantAskDecisionComponent {
  private readonly i18n = inject(I18nService);

  protected readonly testIds = ASK_DECISION_TEST_IDS;

  protected readonly answerControl = new FormControl<string | null>(null);
  protected readonly multiControl = new FormControl<string[]>([], { nonNullable: true });

  protected readonly selected = toSignal(this.answerControl.valueChanges, { initialValue: null });
  protected readonly multiSelected = toSignal(this.multiControl.valueChanges, {
    initialValue: <string[]>[],
  });

  protected readonly useDropdown = computed(() => this.options().length >= DROPDOWN_THRESHOLD);

  protected readonly canConfirm = computed(() =>
    this.multi() ? this.multiSelected().length > 0 : this.selected() != null,
  );

  protected readonly selectItems = computed(
    (): Array<Select2Item<string>> => this.options().map(o => ({ label: o.label, value: o.value })),
  );

  protected readonly radioItems = computed(
    (): Array<RadioGroupItem<string>> =>
      this.options().map(o => ({ option: o.label, value: o.value })),
  );

  protected readonly confirmText = computed(
    () => this.confirmLabel() ?? this.i18n.translate('tlc.tellent_assistant.decision.continue'),
  );

  public readonly question = input.required<string>();
  public readonly options = input.required<AskDecisionOption[]>();
  public readonly confirmLabel = input<string | null>(null);
  public readonly multi = input<boolean>(false);
  public readonly disabled = input<boolean>(false);

  public readonly answered = output<AskDecisionOption[]>();
  public readonly skipped = output<void>();

  protected onSelection(item: Select2Item<string>): void {
    this.answerControl.setValue(item.value);
  }

  protected confirm(): void {
    const chosen = this.multi() ? this.multiChosen() : this.singleChosen();
    if (chosen.length > 0) this.answered.emit(chosen);
  }

  private singleChosen(): AskDecisionOption[] {
    const value = this.selected();
    const option = this.options().find(o => o.value === value);
    return option ? [option] : [];
  }

  private multiChosen(): AskDecisionOption[] {
    const values = new Set(this.multiSelected());
    return this.options().filter(o => values.has(o.value));
  }
}
